<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid positions-modal"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title p-d-flex p-ai-center" v-if="itemData?.id">
        <div>{{ $t('purchaseInvoiceEditModalTitle') }} {{ itemData.number }}</div>
        <ChangedAfterExportIcon :show="itemData.changed_after_export"/>
      </div>
      <div class="p-dialog-title" v-else>{{ $t('purchaseInvoiceCreateModalTitle') }}</div>
    </template>
    <Spinner v-if="dataIsSending"/>
    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label dropdown-wrapper">
          <CustomDropdown id="purchase-invoice-modal-supplier"
                          :disabled="supplierChangingDisabled || requestPositionsAreLoading || reducedPositionsAreLoading || closedFinancialPeriodDataEditingDisabled"
                          ref="selectedSupplierDropdownModal"
                          class="dropdown__select"
                          :class="{'p-invalid' : submitted && !selectedSupplier}"
                          v-model="selectedSupplier"
                          data-key="id"
                          :options="suppliersData"
                          :filterPlaceholder="$t('lineItemSearchSupplierPlaceholder')"
                          :filterFields="['company_name', 'name','reg_number']"
                          :filter="true"
                          :loading="suppliersDataIsLoading || requestPositionsAreLoading || reducedPositionsAreLoading"
                          @change="changeSupplier"
                          @filter="searchSuppliers"
                          @create-new="addNewSupplier"
                          :showClear="!supplierChangingDisabled && !closedFinancialPeriodDataEditingDisabled && !!selectedSupplier">
            <template #value="slotProps">
              <span v-if="slotProps.value">
                {{ slotProps.value.company_name }}
                <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span>
              </span>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div v-if="slotProps.option">
                <div>{{ slotProps.option.company_name }} <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></div>
              </div>
            </template>
          </CustomDropdown>
          <label for="purchase-invoice-modal-supplier">{{ $t('Supplier') }}<span class="warning-color">*</span></label>
          <div class="dropdown__buttons">
            <Button :disabled="supplierChangingDisabled || requestPositionsAreLoading || reducedPositionsAreLoading || closedFinancialPeriodDataEditingDisabled"
                    @click="addNewSupplier"
                    class="dropdown__create-button p-d-flex p-ai-center p-jc-center">
              <i class="ti-plus"/>
            </Button>
          </div>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedSupplier">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <InputText id="invoice_number"
                     @blur="checkNumberExists"
                     @input="invoiceNumberOnInput"
                     :disabled="closedFinancialPeriodDataEditingDisabled"
                     :class="{'p-invalid' : (submitted && !itemData.number) || invoiceNumberExists}"
                     v-model.trim="itemData.number"
                     maxlength="50"
                     autocomplete="off"/>
          <label for="invoice_number">{{$t('Number')}}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.number">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="invoiceNumberExists">{{ $t('invoiceNumberExists') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="purchase-invoice-state"
                          :class="{'p-invalid' : submitted && !selectedState}"
                          v-model="selectedState"
                          :disabled="closedFinancialPeriodDataEditingDisabled"
                          :options="PurchaseInvoiceStates"
                          :preventFilter="true"
                          :filter="false"
                          :showClear="false">
            <!--            optionLabel="label"-->
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span class="state-badge" :class="slotProps.value.color">{{ slotProps.value.label[$i18n.locale] }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span class="state-badge" :class="slotProps.option.color">{{ slotProps.option.label[$i18n.locale] }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="purchase-invoice-state">{{$t('Status')}}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedState">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <Calendar id="purchase-invoice-modal-issued"
                    appendTo="self"
                    class="calendar-bottom"
                    @date-select="changeIssuedDate"
                    @blur="changeIssuedDate"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"
                    v-model="issued"
                    :class="{'p-invalid' : (issued && !issuedDateIsValid) || submitted && !issued ||  (issued && payment_due && payment_due < issued)}"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :disabled="closedFinancialPeriodDataEditingDisabled"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :yearRange="(new Date().getFullYear() - 5) + ':' + new Date().getFullYear()"
                    :selectOtherMonths="true"
                    autocomplete="off"/>
          <label for="purchase-invoice-modal-issued">{{ $t('issuedLabel') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !issued">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="issued && !issuedDateIsValid">{{ $t('Invalid date format') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <Calendar id="purchase-invoice-modal-payment-due"
                    appendTo="self"
                    class="calendar-bottom"
                    @date-select="changePaymentDueDate"
                    @blur="changePaymentDueDate"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"
                    v-model="payment_due"
                    :class="{'p-invalid' : (payment_due && !paymentDueDateIsValid) || submitted && !payment_due || (issued && payment_due && payment_due < issued)}"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :disabled="closedFinancialPeriodDataEditingDisabled"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :yearRange="(new Date().getFullYear() - 5) + ':' + (new Date().getFullYear() + 1)"
                    :selectOtherMonths="true"
                    autocomplete="off"/>
          <label for="purchase-invoice-modal-payment-due">{{ $t('paymentDueLabel') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !payment_due">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="payment_due && !paymentDueDateIsValid">{{ $t('Invalid date format') }}</small>
      </div>


      <div class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-2 small-text"
           v-if="showClosedPeriodEditBanner || showIssuedDateClosedWarning">
        <MessageBar v-if="showClosedPeriodEditBanner"
                    :text="$t('purchaseInvoiceClosedAccountingPeriodAlert')"
                    :accountingPeriodClosedError="true"
                    :fixedToTop="false">
          <template #button v-if="$store.getters.isAccountantOrHigher && !closedFinancialDataEditingEnabled && itemData?.id">
            <Button @click="enableClosedFinancialDataEditing"
                    style="width: auto; color: #fff;"
                    icon="ti-pencil"
                    class="p-button-rounded p-button-sm p-button-outlined p-ml-2"
                    :label="$t('Edit')"/>
          </template>
        </MessageBar>

        <MessageBar v-else-if="showIssuedDateClosedWarning"
                    :text="$t('invoiceIssuedDateClosedAccountingPeriodAlert')"
                    :accountingPeriodClosedError="true"
                    :fixedToTop="false"/>
      </div>

      <ul class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 add-position-buttons background-shema-color" :class="{'add-position-buttons--sticky' : !$store.getters.isMobileLayout}">
        <li>
          <Button @click="addNewPosition(1)"
                  v-tooltip.top="$t('lineItemAddStockItemTooltip')"
                  :disabled="closedFinancialPeriodDataEditingDisabled"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-package"/>
          </Button>
        </li>
        <li>
          <Button @click="addNewPosition(2)"
                  v-tooltip.top="$t('lineItemAddFreePositionTooltip')"
                  :disabled="closedFinancialPeriodDataEditingDisabled"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-pencil"/>
          </Button>
        </li>
        <li>
          <Button @click="addNewPosition(3)"
                  v-tooltip.top="$t('lineItemAddConsumableTooltip')"
                  :disabled="closedFinancialPeriodDataEditingDisabled"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-spray"/>
          </Button>
        </li>
        <li :class="{'cursor-not-allowed' : addRequestPositionsButtonIsDisabled || requestPositionsAreLoading}">
          <Button @click="addRequestPositions"
                  v-tooltip.top="addRequestPositionsButtonIsDisabled ? null : $t('specialOrderPositionsLabel')"
                  :disabled="addRequestPositionsButtonIsDisabled || requestPositionsAreLoading || closedFinancialPeriodDataEditingDisabled"
                  class="p-button-outlined add-position-button"
                  style="min-width: 61.5px">
            <!--                  v-tooltip.top="addRequestPositionsButtonIsDisabled ? null : 'Add request'">-->
            <Spinner v-if="requestPositionsAreLoading" :spinnerStyle="'width: 24px; height: 24px'" :isSmall="true"></Spinner>
            <template v-else>
              <i class="ti-plus p-mr-1"/>
              <i class="ti-truck"/>
            </template>
          </Button>
        </li>
        <li :class="{'cursor-not-allowed' : addReducedPositionsButtonIsDisabled || reducedPositionsAreLoading}">
          <Button @click="addReducedPositions"
                  v-tooltip.top="addReducedPositionsButtonIsDisabled ? null : $t('supplierReturnsLabel')"
                  :disabled="addReducedPositionsButtonIsDisabled || reducedPositionsAreLoading || closedFinancialPeriodDataEditingDisabled"
                  class="p-button-outlined add-position-button"
                  style="min-width: 61.5px">
            <Spinner v-if="reducedPositionsAreLoading" :spinnerStyle="'width: 24px; height: 24px'" :isSmall="true"/>
            <template v-else>
              <i class="ti-minus p-mr-1"/>
              <i class="ti-truck"/>
            </template>
          </Button>
        </li>
        <li>
          <Button @click="addNewPosition(6)"
                  v-tooltip.top="$t('purchaseInvoiceAddReturnRequestTooltip')"
                  :disabled="closedFinancialPeriodDataEditingDisabled"
                  class="p-button-outlined add-position-button warning-color">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-back-left"/>
          </Button>
        </li>
        <li class="p-mr-3"/>
        <li class="modal-input-switch" v-if="accountingIntegrationConnected">
          <div class="p-d-flex p-ai-center">
            <InputSwitch id="export-to-smart-accounts-switch"
                         class="switch-btn"
                         :disabled="exportInvoiceToAccountingDisabled"
                         v-model="exportInvoiceToAccounting"/>
          </div>
          <label for="export-to-smart-accounts-switch"
                 class="switch-label pointer"
                 :class="{'no-pointer': exportInvoiceToAccountingDisabled}">
            {{ accountingExportLabel }}
          </label>
        </li>
      </ul>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5 table-wrapper"
           @dragover.prevent="handleSortableBodyDragOver"
           @drop.prevent="handleSortableBodyDrop">
        <table class="p-datatable p-datatable-striped p-datatable-sm p-datatable-hoverable-rows positions-table">
          <thead class="p-datatable-thead">
          <tr>
            <th class="table-title__icon"></th>
            <th class="table-title__name--first"
                v-if="accountingIntegrationConnected && exportInvoiceToAccounting">
              {{ $t('invoiceExportCodeLabel') }}
            </th>
            <th class="table-title__name--second">{{ $t('Code') }}</th>
            <th class="table-title__name--last">{{ $t('Name') }}</th>
            <th class="table-title__qty">{{ $t('Qty') }}</th>
            <th class="table-title__purchase">{{ $t('purchasePriceShortLabel') }}</th>
            <th v-show="showErrorColumn"></th>
            <th class="table-title__sell">{{ $t('sellingPriceShortLabel') }}</th>
            <th class="table-title__sum">{{ $t('Sum') }}</th>
            <th class="table-title__tax">{{ $t('Tax') }}</th>
            <th class="table-title__total">{{ $t('Total') }}</th>
            <th v-show="invoiceHasNotNewPositions"></th>
            <th v-show="showOrderHeader"></th>
            <th v-show="!closedFinancialPeriodDataEditingDisabled" class="table-title__delete-btn"></th>
          </tr>
          </thead>
          <tbody class="p-datatable-tbody">
          <!--            <tr v-for="(position, index) of positions"-->
          <!--                :key="index">-->
          <tr v-for="(position, index) of positions"
              :key="index"
              class="sortable-row"
              :class="getSortableRowDragClass(index)"
              @dragenter.prevent="handleSortableDragEnter($event, index)"
              @dragover.prevent="handleSortableDragOver($event, index)"
              @dragleave="handleSortableDragLeave($event, index)"
              @drop.prevent="handleSortableDrop($event, index)">
            <template v-if="position.status">
              <!--                <td v-if="!closedFinancialPeriodDataEditingDisabled"-->
              <!--                    :data-index="index"-->
              <!--                    draggable="true"-->
              <!--                    @dragstart="handleDragStart"-->
              <!--                    @dragenter="handleDragEnter"-->
              <!--                    @dragleave="handleDragLeave"-->
              <!--                    @dragover="handleDragOver"-->
              <!--                    @drop="handleDrop"-->
              <!--                    class="grabbable" :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">-->
              <!--                  <div class="p-d-flex p-ai-center grabbable">-->
              <!--                    <div class="grabbable">-->
              <!--                      <i class="grabbable" :class="{-->
              <!--                        'ti-plus p-mr-1' : position.type === 4,-->
              <!--                        'ti-minus p-mr-1' : position.type === 5 || position.type === 7,-->
              <!--                        'ti-plus p-mr-1 warning-color' : position.type === 6-->
              <!--                      }"></i>-->
              <!--                      <i class="grabbable" :class="{-->
              <!--                        'ti-package': position.type === 1,-->
              <!--                        'ti-pencil' : position.type === 2,-->
              <!--                        'ti-spray' : position.type === 3,-->
              <!--                        'ti-truck' : position.type === 4 || position.type === 5 || position.type === 7,-->
              <!--                        'ti-back-left warning-color' : position.type === 6-->
              <!--                      }"></i>-->
              <!--                    </div>-->
              <!--                  </div>-->
              <!--                </td>-->
              <td v-if="!closedFinancialPeriodDataEditingDisabled"
                  class="position-type-sort-cell"
                  :class="{
                      'new-item': position.isNew,
                      'warning-background': position.exceedsFixedQty
                    }">
                <div class="position-sort-wrap">
                  <button type="button"
                          class="position-sort-handle"
                          :class="{ 'is-disabled': !canSortRows() }"
                          :draggable="canSortRows()"
                          :disabled="!canSortRows()"
                          @dragstart="handleSortableDragStart($event, index)"
                          @dragend="handleSortableDragEnd">
                    <span>
                      <i :class="{
                        'ti-plus p-mr-1': position.type === 4,
                        'ti-minus p-mr-1': position.type === 5 || position.type === 7,
                        'ti-plus p-mr-1 warning-color': position.type === 6
                      }"/>

                      <i :class="{
                        'ti-package': position.type === 1,
                        'ti-pencil': position.type === 2,
                        'ti-spray': position.type === 3,
                        'ti-truck': position.type === 4 || position.type === 5 || position.type === 7,
                        'ti-back-left warning-color': position.type === 6
                      }"/>
                    </span>
                  </button>

                  <div class="position-sort-actions" v-if="canSortRows() && showSortableStepButtons()">
                    <button type="button"
                            :disabled="!canMoveSortableRowStep(index, -1)"
                            @click="moveSortableRowStep(index, -1)">
                      <i class="ti-angle-up"/>
                    </button>

                    <button type="button"
                            :disabled="!canMoveSortableRowStep(index, 1)"
                            @click="moveSortableRowStep(index, 1)">
                      <i class="ti-angle-down"/>
                    </button>
                  </div>
                </div>
              </td>
              <!--                <td v-else-->
              <!--                    :data-index="index"-->
              <!--                    :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">-->
              <td v-else
                  class="position-type-sort-cell"
                  :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <div class="p-d-flex p-ai-center">
                  <div>
                    <i :class="{
                        'ti-plus p-mr-1' : position.type === 4,
                        'ti-minus p-mr-1' : position.type === 5 || position.type === 7,
                        'ti-plus p-mr-1 warning-color' : position.type === 6
                      }"/>
                    <i :class="{
                        'ti-package': position.type === 1,
                        'ti-pencil' : position.type === 2,
                        'ti-spray' : position.type === 3,
                        'ti-truck' : position.type === 4 || position.type === 5 || position.type === 7,
                        'ti-back-left warning-color' : position.type === 6
                      }"/>
                  </div>
                </div>
              </td>
              <td v-if="accountingIntegrationConnected && exportInvoiceToAccounting" :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <CustomDropdown :class="{'p-invalid warning-background' : submitted && exportInvoiceToAccounting && !position.selectedAccountingCode}"
                                data-key="code"
                                class="table-body__name-input--accounting-code"
                                v-model="position.selectedAccountingCode"
                                :disabled="closedFinancialPeriodDataEditingDisabled"
                                :options="accountingItems"
                                @change="changeAccountingCode(position)"
                                :placeholder="$t('invoiceSelectExportCodePlaceholder')"
                                :filter="true"
                                :filterFields="['code', 'description']"
                                :showClear="!closedFinancialPeriodDataEditingDisabled">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span v-if="slotProps.value.code">{{ slotProps.value.code }} - </span>{{ slotProps.value.description }}
                    </div>
                    <span v-else>{{ $t('invoiceSelectExportCodePlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div><span v-if="slotProps.option.code">{{ slotProps.option.code }} - </span>{{ slotProps.option.description }}</div>
                  </template>
                </CustomDropdown>
              </td>
              <template v-if="position.type === 1">
                <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                  <div class="dropdown-wrapper table-body__name-input--first">
                    <CustomDropdown class="dropdown__select"
                                    :class="{'p-invalid warning-background' : submitted && !position.selectedWarehouseItem, 'dropdown__select--border-radius' : position.status === 0 || !position.isNew, 'dropdown__select--one-button-width' : position.status !== 0 && position.isNew }"
                                    :disabled="position.status === 0 || !position.isNew || closedFinancialPeriodDataEditingDisabled"
                                    v-model="position.selectedWarehouseItem"
                                    data-key="id"
                                    @change="changeWarehouseItem(position)"
                                    @filter="searchWarehouseItems($event, position)"
                                    :filterPlaceholder="$t('lineItemSearchStockItemPlaceholder')"
                                    :placeholder="$t('lineItemSelectStockItemPlaceholder')"
                                    :options="position.warehouseItemsData || warehouseItemsData"
                                    :filter="true"
                                    :filterFields="['code', 'raw_code', 'name']"
                                    :showClear="false">
                      <template #value="slotProps">
                        <div v-if="slotProps.value">
                          <span>{{ slotProps.value.code }}</span>
                        </div>
                        <span v-else>{{ $t('lineItemSelectStockItemPlaceholder') }}</span>
                      </template>
                      <template #option="slotProps">
                        <div>
                          <div>{{ slotProps.option.name }}<span v-if="slotProps.option.code" style="">, {{$t('Code')}}: {{ slotProps.option.code }}</span></div>
                          <span style="font-size: 0.9rem">
                              {{ $t('Qty') }}: {{ formatDecimal(slotProps.option.qty) }},
                              {{ $t('reservedShortLabel') }}: {{ formatDecimal(slotProps.option.reserved_qty) }},
                              {{ $t('lineItemAvailableQtyLabel') }}: {{ formatDecimal(slotProps.option.qty - slotProps.option.reserved_qty) }}
                            </span>
                        </div>
                      </template>
                    </CustomDropdown>
                    <div v-if="position.status !== 0 && position.isNew && !closedFinancialPeriodDataEditingDisabled" class="p-d-flex p-flex-column">
                      <Button :disabled="!position.selectedWarehouseItem" style="height: 16.5px; border-bottom-right-radius: 0" v-if="position.status !== 0 && position.isNew" @click="editWarehouseItem(position)" class="dropdown__create-button" :icon="'ti-pencil'"/>
                      <Button style="height: 16.5px; border-top-right-radius: 0" v-if="position.status !== 0 && position.isNew" @click="addNewWarehouseItem(position)" class="dropdown__create-button" :icon="'ti-plus'"/>
                    </div>
                  </div>
                </td>
              </template>

              <template v-else>
                <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                  <InputText :placeholder="$t('Code')"
                             :disabled="closedFinancialPeriodDataEditingDisabled"
                             v-model.trim="position.code"
                             maxlength="100"
                             @input="updatePosition(position, $event.target.value, 'code')"
                             class="table-body__name-input--code"
                             autocomplete="off"/>
                </td>
              </template>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <InputText :disabled="(position.type === 1 && !position.selectedWarehouseItem) || position.status === 0 || closedFinancialPeriodDataEditingDisabled"
                           v-model.trim="position.name"
                           maxlength="100"
                           :placeholder="$t('Name')"
                           @input="updatePosition(position, $event.target.value, 'name')"
                           :class="{'p-invalid warning-background' : submitted && !position.name}"
                           class="table-body__name-input--name" autocomplete="off"/>
                <!--                  <InputText :placeholder="$t('Name')" v-model.trim="position.name" @input="updatePosition(position)" :class="{'p-invalid warning-background' : submitted && !position.name}" class="table-body__name-input&#45;&#45;third" autocomplete="off"/>-->
              </td>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <!--                TYPE_WAREHOUSE_ITEM-->
                <InputNumber v-if="position.type === 1"
                             :locale="computedNumberInputLocale"
                             :disabled="!position.isNew || closedFinancialPeriodDataEditingDisabled"
                             @input="updatePosition(position, $event.value, 'qty', 99999, -99999)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 99999, -99999)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             :min="-99999"
                             :max="99999"
                             :showButtons="position.isNew && !closedFinancialPeriodDataEditingDisabled"
                             :minFractionDigits="Number(position.is_decimal) ? 2 : 0"
                             :maxFractionDigits="Number(position.is_decimal) ? 2 : 0"
                             autocomplete="off"/>
                <!--                  REQUEST-->
                <InputNumber v-else-if="position.type === 4"
                             :locale="computedNumberInputLocale"
                             :disabled="!position.isNew || closedFinancialPeriodDataEditingDisabled"
                             @input="updatePosition(position, $event.value, 'qty', position.unfixed_qty, 0)"
                             @update:modelValue="updatePosition(position, $event, 'qty', position.unfixed_qty, 0)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && (!position.qty || ((position.fixedPositionMaxQty || position.fixedPositionMaxQty === 0) && position.qty > position.fixedPositionMaxQty)) }"
                             class="table-body__qty-input"
                             :min="0"
                             :max="position.unfixed_qty"
                             :showButtons="position.isNew && !closedFinancialPeriodDataEditingDisabled"
                             autocomplete="off"/>
                <!--                  RETURN-->
                <InputNumber v-else-if="position.type === 5"
                             :locale="computedNumberInputLocale"
                             :disabled="!position.isNew || closedFinancialPeriodDataEditingDisabled"
                             @input="updatePosition(position, $event.value, 'qty', 0, position.unfixed_qty * -1)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 0, position.unfixed_qty * -1)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && (!position.qty || ((position.fixedPositionMinQty || position.fixedPositionMinQty === 0) && position.qty < position.fixedPositionMinQty))}"
                             class="table-body__qty-input"
                             :min="position.unfixed_qty * -1"
                             :max="0"
                             :showButtons="position.isNew && !closedFinancialPeriodDataEditingDisabled"
                             autocomplete="off"/>
                <!--                  RETURN_REQUEST-->
                <InputNumber v-else-if="position.type === 6"
                             :locale="computedNumberInputLocale"
                             :disabled="!position.isNew || closedFinancialPeriodDataEditingDisabled"
                             @input="updatePosition(position, $event.value, 'qty', 99999, 0)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 99999, 0)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             :min="0"
                             :max="99999"
                             :showButtons="position.isNew && !closedFinancialPeriodDataEditingDisabled"
                             autocomplete="off"/>
                <!--                  WAREHOUSE_REQUEST-->
                <InputNumber v-else-if="position.type === 7"
                             :locale="computedNumberInputLocale"
                             :disabled="!position.isNew || closedFinancialPeriodDataEditingDisabled"
                             @input="updatePosition(position, $event.value, 'qty', position.unfixed_qty, 0)"
                             @update:modelValue="updatePosition(position, $event, 'qty', position.unfixed_qty, 0)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             :min="0"
                             :max="position.unfixed_qty"
                             :showButtons="position.isNew && !closedFinancialPeriodDataEditingDisabled"
                             autocomplete="off"/>
                <!--                OTHER TYPE-->
                <InputNumber v-else
                             :locale="computedNumberInputLocale"
                             :disabled="closedFinancialPeriodDataEditingDisabled"
                             @input="updatePosition(position, $event.value, 'qty', 99999, -99999)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 99999, -99999)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             mode="decimal"
                             :max="99999"
                             :min="-99999"
                             :showButtons="!closedFinancialPeriodDataEditingDisabled"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             autocomplete="off"/>
                <div v-if="position.type === 4 && ((position.fixedPositionMaxQty || +position.fixedPositionMaxQty === 0) && position.qty > position.fixedPositionMaxQty)"
                     class="table-small-text warning-color">
                  <span>{{ $t('maxQtyLabel') }}: {{ position.fixedPositionMaxQty }}</span>
                </div>
                <div v-else-if="position.type === 5 && ((position.fixedPositionMinQty || +position.fixedPositionMinQty === 0) && position.qty < position.fixedPositionMinQty)"
                     class="table-small-text warning-color">
                    <span>
                      {{ $t('minQtyLabel') }}:
                      {{ position.fixedPositionMinQty && +position.fixedPositionMinQty ? position.fixedPositionMinQty : 0 }}
                    </span>
                </div>
              </td>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <InputNumber @input="updatePosition(position, $event.value, 'purchase_price', 1000000, 0)"
                             :locale="computedNumberInputLocale"
                             v-model="position.purchase_price"
                             :disabled="position.type === 1 && !position.isNew || closedFinancialPeriodDataEditingDisabled"
                             :inputClass="{'p-invalid warning-background' : (submitted && !position.purchase_price) || (position.selectedWarehouseItem && position.purchase_price > position.selectedWarehouseItem.purchase_price)}"
                             class="table-body__purchase-input"
                             mode="decimal"
                             :min="0"
                             :max="1000000"
                             :minFractionDigits="2"
                             :maxFractionDigits="6"
                             autocomplete="off"/>
              </td>
              <td v-show="showErrorColumn" class="p-pl-0 p-pr-2" :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <i v-if="position.type === 1 && position.selectedWarehouseItem && position.purchase_price > position.selectedWarehouseItem.purchase_price"
                   class="pi pi-exclamation-circle warning-color"
                   v-tooltip.top="$t('purchasePriceIsHighAlert')">
                </i>
              </td>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <InputNumber :locale="computedNumberInputLocale"
                             @input="updatePosition(position, $event.value, 'sell_price', 1000000, 0)"
                             disabled
                             v-model="position.sell_price"
                             class="table-body__sell-input"
                             mode="decimal"
                             :min="0"
                             :max="1000000"
                             :minFractionDigits="2"
                             :maxFractionDigits="5"
                             autocomplete="off"/>
              </td>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <InputNumber :locale="computedNumberInputLocale" v-model="position.sum" mode="decimal" class="table-body__sum-input" disabled :minFractionDigits="2" :maxFractionDigits="4"/>
              </td>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <CustomDropdown @change="changeTax(position, $event.value)"
                                data-key="id"
                                :disabled="closedFinancialPeriodDataEditingDisabled"
                                class="table-body__tax-input"
                                :class="{'p-invalid warning-background' : submitted && !position.selectedTax}"
                                v-model="position.selectedTax"
                                :options="taxes"
                                :filter="false"
                                optionLabel="value"
                                :showClear="false">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ formatDecimal(slotProps.value.value) }}%</span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div>
                      <span>{{ formatDecimal(slotProps.option.value) }}% <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></span>
                    </div>
                  </template>
                </CustomDropdown>
              </td>
              <td :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <InputNumber :locale="computedNumberInputLocale"
                             v-model="position.total"
                             class="table-body__total-input"
                             mode="decimal"
                             disabled
                             :minFractionDigits="2"
                             :maxFractionDigits="4"/>
              </td>
              <td v-show="invoiceHasNotNewPositions" :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <PositionHistoryButton :position="position"/>
              </td>
              <td v-show="showOrderHeader" :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
<!--                  <span v-if="position.order?.uid" class="p-d-flex p-ai-center">-->
<!--                    <router-link :to="{ path: `/orders/${position.order.uid}` }" target="_blank" draggable="false">-->
<!--                      <Button class="p-button-text p-p-1" v-tooltip.top="position.order?.number ? position.order.number : null">-->
<!--                        <i class="ti-hummer"/>-->
<!--                      </Button>-->
<!--                    </router-link>-->
<!--                  </span>-->
                <OpenOrderButton :position="position" v-show="position.type === 4 || position.type === 5"/>
              </td>
              <td v-show="!closedFinancialPeriodDataEditingDisabled" :class="{'new-item': position.isNew, 'warning-background': position.exceedsFixedQty}">
                <div class="actions">
                  <DeleteButton v-if="!position.isNew" @click="confirmPositionDelete(position)"/>
                  <RemoveButton v-else-if="position.isNew &&
                                           position.type !== 4 &&
                                           position.type !== 5 &&
                                           position.type !== 7"
                                @click="removePosition(position, index)"/>
                  <ReduceButton v-else @click="removePosition(position, index)"/>
                </div>
              </td>
            </template>
          </tr>

          <tr style="background-color: inherit">
            <td style="padding-right: 10px; line-height: 1.5; text-align: right" colspan="15">
              <ul class="p-text-right p-d-flex p-flex-column p-align-end">
                <li class="p-d-flex p-ai-center">
                  <div>{{ $t('Sum without tax') }}: </div>
                  <div class="table-summary__price">{{ formatCurrency(summary.sum) }}</div>
                </li>
                <li class="p-d-flex p-ai-center">
                  <div>{{ $t('Tax') }}: </div>
                  <div class="table-summary__price" v-if="!isTaxCustom">{{ formatCurrency(summary.tax) }}</div>
                  <div class="table-summary__price" v-else-if="isTaxCustom">
                    <InputNumber v-model="summary.tax"
                                 :locale="computedNumberInputLocale"
                                 class="custom-tax-input"
                                 :inputStyle="'text-align:right'"
                                 :disabled="closedFinancialPeriodDataEditingDisabled"
                                 mode="decimal"
                                 @input="summaryTaxOnInput"
                                 :minFractionDigits="2"
                                 :maxFractionDigits="2"/>
                  </div>
                </li>
                <li class="p-d-flex p-ai-center">
                  <div>{{ $t('Manual tax input') }}: </div>
                  <div class="table-summary__price">
                    <Checkbox v-model="isTaxCustom"
                              :disabled="noPositions || closedFinancialPeriodDataEditingDisabled"
                              :min="-maxTaxValue"
                              :max="maxTaxValue"
                              :class="{'no-pointer':noPositions || closedFinancialPeriodDataEditingDisabled}"
                              @change="customTaxCheckboxOnClick"
                              id="custom-tax-input-checkbox"
                              :binary="true" />
                  </div>
                </li>
                <li class="p-d-flex p-ai-center">
                  <div>{{$t('rounding')}}: </div>
                  <div class="table-summary__price">

                    <InputNumber v-model="summary.adjustment"
                                 :locale="computedNumberInputLocale"
                                 class="custom-tax-input"
                                 :disabled="noPositions || closedFinancialPeriodDataEditingDisabled"
                                 @input="summaryAdjustmentOnInput"
                                 :inputStyle="'text-align:right'"
                                 mode="decimal"
                                 :min="-maxAdjustmentValue"
                                 :max="maxAdjustmentValue"
                                 :minFractionDigits="2"
                                 :maxFractionDigits="2"
                                 autocomplete="off"/>
                  </div>
                </li>
                <li class="p-d-flex p-ai-center p-text-bold">
                  <div>{{ $t('Grand total') }}: </div>
                  <div class="table-summary__price">{{ formatCurrency(summary.grand_total) }}</div>
                </li>
              </ul>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <LimitedCharsTextarea :submitted="submitted"
                              :rows="2"
                              v-model="itemData.comment"
                              :maxChars="commentMaxChars"
                              :id="'purchase-invoice-notes'"
                              :label="$t('Notes')"/>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              :class="settings.isDevMode && exportInvoiceToAccounting ? 'p-button-danger p-button-raised': 'p-button-text'"
              @click="saveItem"/>
    </template>
  </Modal>

  <ConfirmDeleteModal :modalDataIsLoading="modalDataIsLoading"
                      :visible="confirmPositionDeleteModal"
                      @close="closeConfirmPositionDeleteModal"
                      @delete-item="deletePosition"
                      :data="positionToDelete?.name"/>

  <!--  @update-items="updateItems"-->
  <SupplierModal :visible="supplierModal"
                 :modalLayer="2"
                 :item="supplier"
                 :taxes="taxesData"
                 @close="closeSupplierModal"
                 @update-item="updateSupplier"/>

  <WarehouseItemModal :visible="warehouseItemModal"
                      :modalLayer="2"
                      :item="warehouseItem"
                      @close="closeWarehouseItemModal"
                      @update-item="updateWarehouseItem"/>

  <RequestPositionsModal :visible="requestPositionsModal"
                         @close="closeRequestPositionsModal"
                         @add-position="addPosition"
                         @remove-position="removeRequestPosition"
                         :removeRequestPositionId="removeRequestPositionId"
                         :supplierId="selectedSupplierId"
                         @disable-add-request-position-button="disableAddRequestPositionButton"
                         @loading-complete="requestPositionsAreLoading = false"
                         ref="requestPositionsModal"/>

  <ReducedPositionsModal :visible="ReducedPositionsModal"
                         @close="closeReducedPositionsModal"
                         @add-position="addPosition"
                         @remove-position="removeReducedPosition"
                         :removeReducedPositionId="removeReducedPositionId"
                         :supplierId="selectedSupplierId"
                         @disable-add-reduced-position-button="disableAddReducedPositionButton"
                         @loading-complete="reducedPositionsAreLoading = false"
                         ref="reducedPositionsModal"/>

  <ConfirmModal :visible="confirmEditClosedFinancialPeriodDataModal"
                :text="$t('closedFinancialPeriodAlert') + ' ' + $t('confirm.proceed')"
                @close="closeConfirmEditClosedFinancialPeriodDataModal"
                @confirm="editClosedFinancialPeriodDataModal"/>

  <ConfirmModal :visible="confirmPurchasePriceIsTooHighModal"
                :text="$t('purchasePriceIsHighAlert') + ' ' + $t('confirm.proceed')"
                @close="closeConfirmPurchasePriceIsTooHighModal"
                @confirm="saveItemAnyway"/>
</template>

<script>
import httpClient from '@/services/http.services'
import SupplierModal from '@/pages/data_directory/components/SupplierModal'
import settings from '@/settings'
import PurchaseInvoiceStates from '@/translations/states/PurchaseInvoiceStates'
import constants from '@/constants'
import PositionHistoryButton from '@/components/PositionHistoryButton'
import RequestPositionsModal from '@/pages/finance/components/RequestPositionsModal'
import ReducedPositionsModal from '@/pages/finance/components/ReducedPositionsModal'
import formatMixins from '@/mixins/formatMixins'
import generateMixins from '@/mixins/generateMixins'
import ReduceButton from '@/components/DataTable/ReduceButton'
import RemoveButton from '@/components/DataTable/RemoveButton'
import WarehouseItemModal from "@/pages/warehouse/components/WarehouseItemModal";
import ReducedPositionReasons from "@/translations/ReducedPositionReasons";
import overlayVisibilityMixins from "@/mixins/overlayVisibilityMixins";
import calculateMixins from "@/mixins/calculateMixins";
import MessageBar from "@/components/MessageBar";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import sortingMixins from "@/mixins/sortingMixins";
import ChangedAfterExportIcon from "@/components/ChangedAfterExportIcon.vue";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import OpenOrderButton from "@/components/OpenOrderButton.vue";

export default {
  mixins: [ formatMixins,
    generateMixins,
    showErrorsMixins,
    overlayVisibilityMixins,
    calculateMixins,
    sortingMixins,
    billingFeaturesMixins
  ],
  components: {
    SupplierModal,
    PositionHistoryButton,
    RequestPositionsModal,
    ReducedPositionsModal,
    ReduceButton,
    RemoveButton,
    WarehouseItemModal,
    MessageBar,
    ChangedAfterExportIcon,
    OpenOrderButton
  },
  // components: { Dropdown },
  emits: ['close', 'update-items', 'update-item'],
  name: 'PurchaseInvoiceModal',
  props: {
    item: Object,
    visible: Boolean,
    taxes: Array,
    suppliers: Array,
    warehouseItems: Array,
  },
  data() {
    return {
      closedFinancialDataEditingEnabled: false,
      ignorePurchasePriceIsTooHigh: false,
      confirmPurchasePriceIsTooHighModal: false,
      requestPositionsAreLoading: false,
      reducedPositionsAreLoading: false,
      addRequestPositionsButtonIsDisabled: false,
      addReducedPositionsButtonIsDisabled: false,
      commentMaxChars: 500,
      warehouseCategories: null,
      exportInvoiceToAccounting: false,
      exportInvoiceToAccountingDisabled: false,
      accountingIntegrationConnected: false,
      accountingIntegrationName: null,
      accountingItems: [],
      accountingDefaults: {
        product: null,
        service: null,
        consumable: null,
      },
      maxAdjustmentValue: 999,
      maxTaxValue: 99999,
      modalDataIsLoading: false,
      suppliersDataIsLoading: false,
      positionIsPushed: true,
      warehouseItemModal: false,
      warehouseItem: null,
      dataIsSending: false,
      issuedDateIsValid: false,
      paymentDueDateIsValid: false,
      isTaxCustom: false,
      suppliersData: [],
      taxesData: [],
      warehouseItemsData: [],
      ReducedPositionsModal: false,
      removeRequestPositionId: null,
      removeReducedPositionId: null,
      requestPositionsModal: false,
      invoiceNumberExists: false,
      disableSaveButton: false,
      issued: null,
      payment_due: null,
      itemData: {},
      settings,
      selectedSupplier: null,
      selectedState: null,
      positionToUpdate: null,
      submitted: false,
      supplierModal: false,
      supplier: null,
      confirmPositionDeleteModal: false,
      positionToDelete: {},
      positions: [],
      defaultTaxId: null,
      summary: {
        sum: null,
        tax: null,
        grand_total: null
      },
      PurchaseInvoiceStates,
      constants,
      ReducedPositionReasons,
      financialPeriodHasBeenClosed: false,
      issuedDateFinancialPeriodHasBeenClosed: false,
      // editClosedFinancialPeriodDataConfirmed: false,
      confirmEditClosedFinancialPeriodDataModal: false,
    }
  },
  watch: {
    suppliers: {
      immediate: true,
      handler(value) {
        this.suppliersData = (value || []).map(m => ({
          ...m,
          id: +m.id || null,
          days_to_pay: +m.days_to_pay || null,
          tax_id: +m.tax_id || null,
        }))
      }
    },
    taxes: {
      immediate: true,
      handler(value) {
        this.taxesData = (value || []).map(m => ({
          ...m,
          id: +m.id || null,
          is_default: +m.is_default || 0,
          status: +m.status || 0,
        }))
      }
    },
    warehouseItems: {
      immediate: true,
      handler(value) {
        this.warehouseItemsData = (value || []).map(m => ({ ...m, id: +m.id || null }))
      }
    },
    item(value) {
      if (!value) return false

      this.itemData = { ...value }
      this.exportInvoiceToAccounting = this.accountingIntegrationConnected && this.getInitialAccountingExportValue()

      this.summary.sum = value.sum ? +value.sum : 0
      this.summary.tax = value.tax ? +value.tax : 0
      this.summary.adjustment = value.adjustment ? +value.adjustment : 0
      this.summary.grand_total = value.grand_total ? +value.grand_total : 0

      if (value.is_tax_custom) {
        this.isTaxCustom = true
      }

      this.itemData.originalNumberLoweredCase = this.normalizeInvoiceNumber(value.number)
      this.itemData.originalSupplierId = value.supplier_id ? +value.supplier_id : (value.supplier?.id ? +value.supplier.id : null)

      this.selectedSupplier = value.supplier ?? null

      if (value.issued) {
        this.issued = new Date(value.issued * 1000)

        const lockTs = this.canUsePeriodLock
            ? this.$store.getters.locationData?.financial_period_locked_until || null
            : null

        this.financialPeriodHasBeenClosed = !!lockTs && !!value.issued && value.issued <= lockTs
      } else {
        this.issued = new Date()
        this.financialPeriodHasBeenClosed = false
      }

      if (!this.financialPeriodHasBeenClosed) {
        if (value.isNew || value.id) {
          this.getRequestPositions()
          this.getReducedPositions()
        }
      }

      this.checkIssuedDateIsValid()

      if (value.payment_due) {
        this.payment_due = new Date(value.payment_due * 1000)
      } else {
        this.calculatePaymentDue()
      }

      this.checkPaymentDueDateIsValid()

      if (this.item.state) {
        this.selectedState = this.PurchaseInvoiceStates.find(state => state.num === value.state)
      } else {
        this.selectedState = this.PurchaseInvoiceStates[0]
      }

      if (value.positions) {
        this.positions = value.positions.map(position => {
          const selectedTax = this.findSelectedTax(position)
          const selectedWarehouseItem = position.type === 1 || position.type === 7 ? this.findSelectedWarehouseItem(position) : null

          const originalAccountingCode = position.accounting_code ?? null;
          const selectedAccountingCode = this.accountingIntegrationConnected ? this.findAccountingItem(position) : null
          return {
            selectedAccountingCode: selectedAccountingCode,
            accounting_code: position.accounting_code ?? null,
            originalAccountingCode: originalAccountingCode,

            id: position.id,
            isUpdated: position.accounting_code !== originalAccountingCode,
            order_id: position.order_id,
            order: position.order,
            order_position_id: position.order_position_id,
            reduced_position_id: position.reduced_position_id,
            stock_position_id: position.stock_position_id,
            type: position.type,
            sorting: position.sorting ?? null,
            name: position.name,
            code: position.code ? position.code : null,
            raw_code: position.raw_code ? position.raw_code : null,
            qty: position.qty ? +position.qty : 0,
            purchase_price: position.purchase_price ? +position.purchase_price : 0,
            sell_price: position.sell_price ? +position.sell_price : 0,
            sum: position.sum ? +position.sum : 0,
            total: position.total ? +position.total : 0,
            warehouse_item_id: position.warehouse_item_id,
            selectedWarehouseItem: selectedWarehouseItem,
            is_decimal: selectedWarehouseItem ? selectedWarehouseItem.is_decimal && +selectedWarehouseItem.is_decimal : false,
            selectedTax: selectedTax,
            tax_id: position.tax_id ?? selectedTax?.id ?? null,
            // tax_value: position.tax_value ?? selectedTax.value ?? 0,
            tax_value: selectedTax?.value ? +selectedTax.value : 0,
            tax_sum: position.tax_sum ? +position.tax_sum : 0,
            state: position.state ? +position.state : null,
            status: position.status,
            created_at: position.created_at ? position.created_at : null,
            creator: position.creator ? position.creator : null,
          }
        })
      }

      // this.positions.forEach(position => {
      //   this.calculatePositionSummary(position)
      // })

      this.updateSummary()
    },
    visible(value) {
      if (!value) {
        this.closedFinancialDataEditingEnabled = false
        this.ignorePurchasePriceIsTooHigh = false
        this.addRequestPositionsButtonIsDisabled = false
        this.addReducedPositionsButtonIsDisabled = false
        this.clearRequestAndReducedPositionsModalData()
        this.$refs.selectedSupplierDropdownModal?.clearFilterValue()
        this.disableSaveButton = false
        this.dataIsSending = false

        this.submitted = false
        this.selectedState = null
        this.positions = []
        this.invoiceNumberExists = false
        this.itemData = null
        this.isTaxCustom = false
        this.issuedDateIsValid = false
        this.paymentDueDateIsValid = false
        this.exportInvoiceToAccounting = false
        this.exportInvoiceToAccountingDisabled = false
        this.resetAccountingExportConfig()
        this.suppliersData = [...this.suppliers]

        this.financialPeriodHasBeenClosed = false
        this.issuedDateFinancialPeriodHasBeenClosed = false

      } else {
        this.loadAccountingExportConfig()
      }
    },
    '$store.state.accountingIntegration'(integration) {
      if (this.visible && !integration?.directions?.purchase?.configured) {
        this.resetAccountingExportConfig()
      }
    },
    '$store.state.secondLayerIsOpened'() {
      if (!this.$store.state.secondLayerIsOpened) {
        if (this.confirmPositionDeleteModal) {
          this.closeConfirmPositionDeleteModal()
        }
        if (this.supplierModal) {
          this.closeSupplierModal()
        }
        if (this.requestPositionsModal) {
          this.closeRequestPositionsModal()
        }
        if (this.ReducedPositionsModal) {
          this.closeReducedPositionsModal()
        }
        if (this.warehouseItemModal) {
          this.closeWarehouseItemModal()
        }
        if (this.confirmEditClosedFinancialPeriodDataModal) {
          this.closeConfirmEditClosedFinancialPeriodDataModal()
        }
        if (this.confirmPurchasePriceIsTooHighModal) {
          this.closeConfirmPurchasePriceIsTooHighModal()
        }
        if (this.overdueTaxModal) {
          this.closeOverdueTaxModal()
        }
      }
    },
  },
  methods: {
    getInitialAccountingExportValue() {
      // if (settings.isDevMode) return false
      if (this.itemData?.isNew) {
        return Number(
            this.$store.getters.locationData?.default_export_purchase_invoice ?? 0
        ) === 1
      }

      const storedValue = this.itemData?.export_to_accounting

      if (storedValue !== undefined && storedValue !== null) {
        return storedValue === true || Number(storedValue) === 1
      }

      return false
    },
    resetAccountingExportConfig() {
      this.accountingIntegrationConnected = false
      this.accountingIntegrationName = null
      this.accountingItems = []
      this.accountingDefaults = {
        product: null,
        service: null,
        consumable: null,
      }
      this.exportInvoiceToAccounting = false
    },
    loadAccountingExportConfig() {
      this.resetAccountingExportConfig()

      const integration = this.$store.getters.accountingIntegration
      const config = integration?.directions?.purchase
      if (!integration || !config?.configured) return

      this.accountingIntegrationConnected = true
      this.accountingIntegrationName = integration.provider_name || null
      this.accountingItems = Array.isArray(config.codes)
          ? config.codes.map(item => ({
            code: String(item?.code ?? ''),
            description: String(item?.description ?? ''),
          })).filter(item => item.code && item.description)
          : []
      this.accountingDefaults = {
        product: config.defaults?.product ?? null,
        service: config.defaults?.service ?? null,
        consumable: config.defaults?.consumable ?? null,
      }

      this.exportInvoiceToAccounting = this.getInitialAccountingExportValue()
      this.exportInvoiceToAccountingDisabled = false
      this.applyAccountingConfigToPositions()
    },
    applyAccountingConfigToPositions() {
      if (!this.accountingIntegrationConnected) return

      this.positions?.forEach(position => {
        const previousCode = position.accounting_code == null
            ? null
            : String(position.accounting_code)
        const selected = this.findAccountingItem(position)

        position.selectedAccountingCode = selected
        position.accounting_code = selected?.code ?? null

        if (!position.isNew && previousCode !== position.accounting_code) {
          position.isUpdated = true
        }
      })
    },
    enableClosedFinancialDataEditing() {
      this.closedFinancialDataEditingEnabled = true

      this.getRequestPositions()
      this.getReducedPositions()
    },
    setNewSorting() {
      let sorting = 1
      this.positions.forEach(p => {
        p.sorting = sorting++
        p.isUpdated = !p.isNew
        // this.checkPositionHasBeenUpdated(p)
      })
    },
    saveItemAnyway() {
      this.ignorePurchasePriceIsTooHigh = true
      this.closeConfirmPurchasePriceIsTooHighModal()
      this.saveItem()
    },
    openConfirmPurchasePriceIsTooHighModal() {
      this.confirmPurchasePriceIsTooHighModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    closeConfirmPurchasePriceIsTooHighModal() {
      this.confirmPurchasePriceIsTooHighModal = false
      this.$store.commit('toggleSecondLayer', false)

    },
    disableAddRequestPositionButton(boolean) {
      this.addRequestPositionsButtonIsDisabled = boolean
    },
    disableAddReducedPositionButton(boolean) {
      this.addReducedPositionsButtonIsDisabled = boolean
    },
    closeConfirmEditClosedFinancialPeriodDataModal() {
      this.confirmEditClosedFinancialPeriodDataModal = false
      this.$store.commit('toggleSecondLayer', false)

    },
    openConfirmEditClosedFinancialPeriodDataModal() {
      this.confirmEditClosedFinancialPeriodDataModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    editClosedFinancialPeriodDataModal() {
      this.closeConfirmEditClosedFinancialPeriodDataModal()
      // this.editClosedFinancialPeriodDataConfirmed = true
      this.saveItem()
    },
    generateNewPositionsData() {
      const positions = this.positions?.filter(position => position.isNew)
      if (!positions?.length) return null

      const newPosition = this.generatePositionsData(positions)
      return newPosition
    },
    generateUpdatedPositionsData() {
      const positions = this.positions?.filter(position => position.isUpdated && position.status)
      if (!positions?.length) return null

      const updatedPosition = this.generatePositionsData(positions)
      return updatedPosition
    },
    generatePositionsData(positions) {
      const positionData = positions.map(position => {
        return {
          id: position.id,
          // temp_id: position.temp_id,
          // purchase_invoice_id: position.purchase_invoice_id,
          order_position_id: position.order_position_id,
          reduced_position_id: position.reduced_position_id,
          warehouse_item_id: position.warehouse_item_id,
          type: position.type,
          accounting_code: position.accounting_code ?? null,
          code: position.code ? position.code.trim() : null,
          raw_code: position.raw_code,
          name: position.name ? position.name.trim() : null,
          qty: position.qty,
          purchase_price: position.purchase_price,
          sell_price: position.sell_price,
          tax_id: position.tax_id,
          tax_value: position.tax_value,
          tax_sum: position.tax_sum,
          sum: position.sum,
          total: position.total,
          status: position.status,
          sorting: position.sorting
        }
      })
      return positionData
    },
    findAccountingItem(position) {
      const currentAccountingCode = position?.accounting_code == null
          ? null
          : String(position.accounting_code)
      const accountingItem = currentAccountingCode === null
          ? null
          : this.accountingItems.find(item => String(item.code) === currentAccountingCode)

      let selectedAccountingCode = null
      if (accountingItem) {
        position.accounting_code = accountingItem.code
        selectedAccountingCode = accountingItem
      } else {
        const defaultAccountingItem = this.findDefaultAccountingItem(position)
        if (defaultAccountingItem) {
          position.accounting_code = defaultAccountingItem.code
          selectedAccountingCode = defaultAccountingItem
        }
      }
      const resultingCode = position.accounting_code == null
          ? null
          : String(position.accounting_code)
      if (!position.isNew && currentAccountingCode !== resultingCode) {
        position.isUpdated = true
      }

      return selectedAccountingCode
    },
    findDefaultAccountingItem(position) {
      let positionType = 'product'
      if (position.type === 3) {
        positionType = 'consumable'
      } else if (position.type === 2) {
        positionType = 'service'
      }

      const defaultCode = this.accountingDefaults[positionType]
      if (defaultCode == null) return null

      return this.accountingItems.find(item => String(item.code) === String(defaultCode)) ?? null
    },
    changeAccountingCode(position) {
      // if (!position.selectedAccountingCode) return false
      position.isUpdated = !position.isNew
      position.accounting_code = position.selectedAccountingCode?.code ?? null
    },
    changeIssuedDate() {
      if (!this.issued) return false
      this.issued = this.calculateDateWithCurrentTime(this.issued)

      this.checkIssuedDateIsValid()
      this.calculatePaymentDue()
      this.checkIssuedDateFinancialPeriodIsClosed()
    },
    checkIssuedDateFinancialPeriodIsClosed() {
      const lockTs = this.canUsePeriodLock
          ? this.$store.getters.locationData?.financial_period_locked_until || null
          : null

      const issuedTs = this.issued instanceof Date ? Math.floor(this.issued.getTime() / 1000) : null

      this.issuedDateFinancialPeriodHasBeenClosed =
          !!lockTs && !!issuedTs && issuedTs <= lockTs

      this.disableSaveButton =
          !this.$store.getters.isAccountantOrHigher &&
          this.issuedDateFinancialPeriodHasBeenClosed &&
          !this.financialPeriodHasBeenClosed
    },
    changePaymentDueDate() {
      if (!this.payment_due) return false
      this.payment_due = this.calculateDayEndDate(this.payment_due)
      this.checkPaymentDueDateIsValid()
    },
    checkIssuedDateIsValid() {
      this.issuedDateIsValid = this.checkDateIsValid(this.issued)
    },
    checkPaymentDueDateIsValid() {
      this.paymentDueDateIsValid = this.checkDateIsValid(this.payment_due)
    },
    checkDateIsValid(date) {
      if (!date) return false
      return !!(date instanceof Date && !isNaN(date))
    },
    calculatePaymentDue() {
      if (!this.issued || !this.issuedDateIsValid) return false

      if (this.selectedSupplier && this.selectedSupplier.days_to_pay) {
        this.payment_due = new Date(+this.issued + (this.selectedSupplier.days_to_pay * 24 * 60 * 60 * 1000))
      } else {
        this.payment_due = this.issued
      }
      this.checkPaymentDueDateIsValid()
      this.changePaymentDueDate()
    },
    summaryTaxOnInput($event) {
      let value = $event.value
      if (value === '-' || !value) {
        value = 0
      }
      if (value > this.maxTaxValue) {
        value = this.maxTaxValue
      } else if (value < -this.maxTaxValue) {
        value = -this.maxTaxValue
      }

      this.summary.tax = value
      this.updateSummary()
    },
    summaryAdjustmentOnInput($event) {
      let value = $event.value
      if (value === '-' || !value) {
        value = 0
      }
      if (value > this.maxAdjustmentValue) {
        value = this.maxAdjustmentValue
      } else if (value < -this.maxAdjustmentValue) {
        value = -this.maxAdjustmentValue
      }

      this.summary.adjustment = value
      this.updateSummary()
    },
    customTaxCheckboxOnClick() {
      if (!this.isTaxCustom) {
        this.updateSummary()
      }
    },
    findSelectedWarehouseItem(position) {
      if (!position.warehouseItem?.id) return false
      const warehouseItem = this.warehouseItemsData.find(item => +item.id === +position.warehouseItem.id)
      if (warehouseItem) {
        return warehouseItem
      } else {
        this.warehouseItemsData.push(position.warehouseItem)
        return position.warehouseItem
      }
    },
    findSelectedTax(position) {
      if (!position.tax?.id) return false
      const tax = this.taxesData.find(tax => +tax.id === +position.tax.id)
      if (tax) {
        return tax
      } else {
        this.taxesData.push(position.tax)
        return position.tax
      }
    },
    async addPosition(position, type) {
      if (!position || !type) return false

      if (this.selectedSupplier && position.supplier_id !== this.selectedSupplierId) return

      if (!this.selectedSupplier ||
          (this.selectedSupplierId !== position.supplier?.id)) {
        this.selectedSupplier = position.supplier
        this.changeSupplier()
      }

      const reducedPositionData = position.position_data
      const purchasePrice = reducedPositionData ? (reducedPositionData?.purchase_price ? +reducedPositionData.purchase_price : 0) : (position.purchase_price ? +position.purchase_price : 0 )
      // const sellPrice = reducedPositionData ? (reducedPositionData?.sell_price ? +reducedPositionData.sell_price : 0) : (position.sell_price ? +position.sell_price : 0)

      const positionQty = position.qty ? +position.qty : 0
      const orderedQty = position.ordered_qty ? +position.ordered_qty : 0
      const fixedQty = position.fixed_qty ? +position.fixed_qty : 0
      const unfixedQty = type === 4 ? orderedQty - fixedQty : positionQty - fixedQty

      let newActualQty = 0
      if (type === 4) { //Request
        newActualQty = orderedQty - fixedQty
      } else if (type === 5) { //Return
        newActualQty = (positionQty - fixedQty) * -1
      } else if (type === 7) { //Warehouse request
        newActualQty = positionQty - fixedQty
      }


      let sellPrice = 0
      if (type === 4) { //Order position type request
        // sellPrice = +positionQty ? (position.sum / positionQty) : (position.sell_price ? +position.sell_price : 0)
        sellPrice = +positionQty ? (position.sum / positionQty) : 0
        // } else if (type === 5 || type === 7) { //Return request || warehouse request
      } else if (type === 5) { //Return request
        sellPrice = position.sell_price ? +position.sell_price : 0
        if (reducedPositionData) {
          if (reducedPositionData?.qty && reducedPositionData?.sum) {
            if (!+reducedPositionData.qty) {
              sellPrice = 0
            } else {
              sellPrice = +reducedPositionData.sum / +reducedPositionData.qty
            }
          } else {
            sellPrice = reducedPositionData?.sell_price ? +reducedPositionData.sell_price : 0
          }
        }
      }

      this.pickTax(position)

      const positionSum = purchasePrice * newActualQty
      const positionTotal = purchasePrice * newActualQty * ((position.tax_value / 100) + 1)
      const positionTaxSum = (purchasePrice * newActualQty) * (position.tax_value / 100)

      const newPosition = {
        // id: type === 4 || type === 5 ? 'temp_' + Date.now() + '_' + Math.floor(Math.random() * 40) : null,
        type: type,
        order_id: position.order?.id,
        order: position.order,
        sorting: this.findPositionsLastSortingValue(),
        name: reducedPositionData ? reducedPositionData.name : position.name,
        code: reducedPositionData ? reducedPositionData.code : position.code,
        raw_code: reducedPositionData ? this.generateRawCode(reducedPositionData.code) : this.generateRawCode(position.code),
        purchase_price: purchasePrice,
        sell_price: sellPrice,
        supplier_id: position.supplier?.id,
        isUpdated: false,
        state: position.state ? +position.state : null,
        // status: position.status,
        status: 1,
        // created_at: position.created_at ? position.created_at : null,
        // creator: position.creator ? position.creator : null,
        isNew: true,
        selectedTax: position.selectedTax,
        tax_id: position.tax_id,
        tax_value: position.tax_value,
        order_position_id: reducedPositionData  ? position.order_position_id : position.id,
        reduced_position_id: reducedPositionData ? position.id : null,
        qty: newActualQty,
        fixed_qty: fixedQty,
        unfixed_qty: unfixedQty,
        sum: this.formatDecimal(positionSum, false),
        total: this.formatDecimal(positionTotal, false),
        tax_sum: this.formatDecimal(positionTaxSum, false)
      }

      // console.log(type)  !!!! DELETE???
      if (type === 7) {
        let searchString = newPosition.raw_code ?? newPosition.name ?? null
        if (searchString) {
          const result = await this.tryToFindWarehouseItem(searchString)
          // console.log(result)

          newPosition.searchData = searchString

          if (result) {
            newPosition.warehouseItemsData = result
            if (result.length === 1) {
              const selectedWarehouseItem = result[0]
              this.changeReducedWarehouseItem(newPosition, selectedWarehouseItem)

            }
          }
        }
      }

      if (this.accountingIntegrationConnected) {
        const accountingItem = this.findDefaultAccountingItem(newPosition)
        if (accountingItem) {
          newPosition.selectedAccountingCode = accountingItem
          newPosition.accounting_code = accountingItem.code
        }
      }

      this.positions.push(newPosition)
      this.updateSummary()
    },
    changeReducedPositionReason(position) {
      position.reasonId = position.selectedReason?.id
    },
    changeReducedWarehouseItem(position, selectedWarehouseItem = null) {
      const newSelectedWarehouseItem = selectedWarehouseItem ?? position.selectedWarehouseItem ?? null
      if (!newSelectedWarehouseItem) return false
      position.selectedWarehouseItem = newSelectedWarehouseItem
      position.warehouse_item_id = newSelectedWarehouseItem.id
    },
    async tryToFindWarehouseItem(searchString) {
      if (!searchString) return null
      // searchString = '141224141244142214124'
      let result = null
      try {
        const { status, data } = await httpClient('warehouse/get-dropdown-items?search=' + searchString)

        if (status === 200 && data?.success) {
          result = data.items && Array.isArray(data.items) ? data.items : null
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      }
      return result
    },
    invoiceNumberOnInput() {
      this.invoiceNumberExists = false
    },
    normalizeInvoiceNumber(value) {
      return String(value ?? '').trim().toLowerCase()
    },

    invoiceNumberIsUnchanged() {
      if (!this.itemData?.id) return false

      return this.normalizeInvoiceNumber(this.itemData.number) === this.itemData.originalNumberLoweredCase &&
          +this.selectedSupplierId === +this.itemData.originalSupplierId
    },
    // async checkNumberExists() {
    //
    //   if (!this.selectedSupplier?.id || !this.itemData.number) {
    //     this.invoiceNumberExists = false
    //     return
    //   }
    //
    //   if (this.invoiceNumberIsUnchanged()) {
    //     this.invoiceNumberExists = false
    //     return
    //   }
    //
    //   try {
    //     const { status, data } = await httpClient.post('purchase-invoice/check-number-exists', {
    //       id: this.itemData?.id || null,
    //       number: this.itemData.number,
    //       supplierId: this.selectedSupplierId
    //     })
    //
    //     if (status === 200 && data) {
    //       this.invoiceNumberExists = !!data.exists
    //     }
    //   } catch(err) {
    //     this.showError(err)
    //   }
    // },

    async checkNumberExists() {
      const supplierId = this.selectedSupplierId
      const number = this.itemData.number ? this.itemData.number.trim() : ''

      this.invoiceNumberExists = false

      if (!supplierId || !number) {
        return
      }

      if (this.invoiceNumberIsUnchanged()) {
        return
      }

      try {
        const { status, data } = await httpClient.post('purchase-invoice/check-number-exists', {
          id: this.itemData?.id || null,
          number,
          supplierId
        })

        const sameNumber =
            this.normalizeInvoiceNumber(this.itemData.number) === this.normalizeInvoiceNumber(number)

        const sameSupplier =
            +this.selectedSupplierId === +supplierId

        if (status === 200 && data && sameNumber && sameSupplier) {
          this.invoiceNumberExists = !!data.exists
        }
      } catch(err) {
        this.showError(err)
      }
    },

    async searchWarehouseItems(event, position) {
      if (!this.$store.getters.userCanGetData) return

      const search = String(event?.value ?? '').trim()

      if (!search) {
        position.warehouseItemsData = this.warehouseItemsData
        return
      }

      try {
        const { status, data } = await httpClient(
            'warehouse/get-dropdown-items?search=' + encodeURIComponent(search)
        )

        if (status === 200 && data?.success) {
          position.warehouseItemsData = Array.isArray(data.items) ? data.items : []
        } else {
          position.warehouseItemsData = []
        }
      } catch (err) {
        position.warehouseItemsData = []
        this.showError(err)
      }
    },

    async searchSuppliers(event) {
      const search = String(event?.value ?? '').trim()

      this.suppliersDataIsLoading = true

      if (!search) {
        this.suppliersData = []

        if (this.selectedSupplier) {
          this.suppliersData.push(this.selectedSupplier)
        }

        this.suppliersDataIsLoading = false
        return
      }

      try {
        const { status, data } = await httpClient(
            'supplier/get-suppliers?search=' + encodeURIComponent(search)
        )

        if (status === 200 && data?.success) {
          this.suppliersData = Array.isArray(data.items) ? data.items : []
        } else {
          this.showUnknownError()
          this.suppliersData = []
        }
      } catch (err) {
        this.suppliersData = []
        this.showError(err)
      } finally {
        this.suppliersDataIsLoading = false

        if (this.selectedSupplier) {
          const selectedSupplierInArray = this.suppliersData.some(
              s => +s.id === +this.selectedSupplierId
          )

          if (!selectedSupplierInArray) {
            this.suppliersData.push(this.selectedSupplier)
          }
        }
      }
    },

    addNewSupplier() {
      this.supplierModal = true
      this.supplier = {}
      this.$store.commit('toggleSecondLayer', true)
    },
    updateItem() {
      const id = this.itemData?.id
      if (!id) return
      this.$emit('update-item', id)
    },
    updateSupplier(supplier) {
      this.selectedSupplier = supplier
      const supplierInList = !!this.suppliersData?.find(i => i.id === supplier.id)
      if (supplier.isNew || !supplierInList) {
        this.suppliersData.push(supplier)
      }
      this.searchData = null
      this.changeSupplier()
    },
    changeSupplier() {
      this.isLoading = true
      if (!this.selectedSupplier) {
        this.$refs.selectedSupplierDropdownModal?.clearFilterValue()
        this.suppliersData = [...this.suppliers]
      }

      this.clearRequestAndReducedPositionsModalData(false, false)
      this.calculatePaymentDue()
      this.checkNumberExists()
      this.getRequestPositions()
      this.getReducedPositions()
    },
    getRequestPositions() {
      this.requestPositionsAreLoading = true
      setTimeout(() => {
        this.$refs.requestPositionsModal?.getItems?.()
      }, 100)
    },
    getReducedPositions() {
      this.reducedPositionsAreLoading = true
      setTimeout(() => {
        this.$refs.reducedPositionsModal?.getItems?.()
      }, 100)
    },
    clearRequestAndReducedPositionsModalData(clearAddedItems = true, clearSearchingData = true) {
      this.$refs.requestPositionsModal?.clearModalData(clearAddedItems, clearSearchingData)
      this.$refs.reducedPositionsModal?.clearModalData(clearAddedItems, clearSearchingData)
    },
    addNewWarehouseItem(position, isReducedPosition = false) {
      if (isReducedPosition) {
        this.warehouseItem = {
          code: position.code,
          raw_code: position.raw_code ? position.raw_code : (position.code ? this.generateRawCode(position.code) : null),
          name: position.name,
          purchase_price: position.purchase_price,
          sell_price: position.sell_price,
        }
      } else {
        this.warehouseItem = {}
      }
      this.position = position
      this.warehouseItemModal = true

      this.$store.commit('toggleSecondLayer', true)
    },
    editWarehouseItem(position) {
      if (!position) return
      this.position = position
      this.warehouseItem = position.selectedWarehouseItem
      this.warehouseItemModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    closeWarehouseItemModal() {
      this.warehouseItem = {}
      this.position = {}
      this.warehouseItemModal = false
      this.$store.commit('toggleSecondLayer', false)
    },
    updateWarehouseItem(warehouseItem) {
      if (!warehouseItem) return false
      this.position.selectedWarehouseItem = {...warehouseItem}
      let warehouseItemInList = this.warehouseItemsData?.find(i => i.id === warehouseItem.id)
      if (warehouseItem.isNew || !warehouseItemInList) {
        if (!this.warehouseItemsData?.length) {
          this.warehouseItemsData = [{...warehouseItem}]
        } else {
          this.warehouseItemsData.push({...warehouseItem})
        }
      } else {
        const indexOfFoundWarehouseItem = this.warehouseItemsData.indexOf(warehouseItemInList)
        this.warehouseItemsData[indexOfFoundWarehouseItem] = this.position.selectedWarehouseItem
      }
      this.changeWarehouseItem(this.position)
    },
    changeWarehouseItem(position) {
      position.isUpdated = !position.isNew
      const newWarehouseItem = position.selectedWarehouseItem
      if (newWarehouseItem) {
        position.is_decimal = newWarehouseItem.is_decimal && +newWarehouseItem.is_decimal
        position.qty = position.is_decimal ? this.formatDecimal(position.qty, false) : this.formatDecimal(position.qty, false, 0)
        position.code = newWarehouseItem.code
        position.raw_code = newWarehouseItem.raw_code ? newWarehouseItem.raw_code : this.generateRawCode(newWarehouseItem.code)
        position.name = newWarehouseItem.name
        position.purchase_price = newWarehouseItem.purchase_price ? +newWarehouseItem.purchase_price : 0
        position.sell_price = newWarehouseItem.sell_price ? +newWarehouseItem.sell_price : 0
        position.warehouse_item_id = newWarehouseItem.id
        // position.selectedWarehouseItem.qty -= position.qty
      } else {
        position.is_decimal = false
        position.code = null
        position.raw_code = null
        position.name = null
        position.purchase_price = null
        position.sell_price = null
        position.warehouse_item_id = null
      }

      this.calculatePositionSummary(position)
      // this.updateConsumable()
      this.updateSummary()
    },
    closeSupplierModal() {
      this.supplierModal = false
      this.supplier = {}
      // if (this.$store.state.secondLayerIsOpened) {
      this.$store.commit('toggleSecondLayer', false)
      // }
    },
    // async getUndefinedSupplier(supplierId) {
    //   try {
    //     const { status, data } = await httpClient.post('supplier/get-supplier-by-id', { id: supplierId })
    //     if (status === 200) {
    //       if (data) {
    //         if (!this.suppliersData.find(supplier => supplier.id === supplierId)) {
    //           this.suppliersData.push(data)
    //         }
    //         this.selectedSupplier = data
    //       } else {
    //         this.selectedSupplier = null
    //       }
    //       this.changeSupplier()
    //     }
    //   } catch(err) {
    //     this.showError(err)
    //   }
    // },
    addNewPosition(type) {
      const position = {
        type: type,
        name: '',
        qty: 1,
        sum: 0,
        total: 0,
        status: 1,
        isNew: true,
        sorting: this.findPositionsLastSortingValue(),
      }

      if (this.accountingIntegrationConnected) {
        const accountingItem = this.findDefaultAccountingItem(position)
        if (accountingItem) {
          position.selectedAccountingCode = accountingItem
          position.accounting_code = accountingItem.code
        }
      }
      this.pickTax(position)

      if (type === 2) {
        position.state = 1
      }

      this.positions.push(position)
    },
    findPositionsLastSortingValue() {
      if (!this.positions?.length) return 1
      let maxValue = 1;
      const values = Object.values(this.positions);
      values.map((el) => {
        const valueFromObject = el.sorting;
        maxValue = Math.max(maxValue, valueFromObject);
      });
      if (maxValue < this.positions.length) {
        maxValue = this.positions.length
      }
      ++maxValue
      return maxValue
    },
    pickTax(position) {
      const selectedSupplierTax = this.selectedSupplier?.tax_id ? this.taxesData?.find(tax => +tax.id === +this.selectedSupplier.tax_id) : null
      if (selectedSupplierTax) {
        position.selectedTax = selectedSupplierTax
        position.tax_id = selectedSupplierTax.id
        position.tax_value = selectedSupplierTax.value
      } else {
        const tenantCompanyDefaultTaxId = this.$store.getters.locationData?.default_tax_id ? +this.$store.getters.locationData.default_tax_id : null
        if (!tenantCompanyDefaultTaxId) return
        const defaultTax = this.taxesData?.find(tax => +tax.id === +tenantCompanyDefaultTaxId) ?? null
        if (defaultTax) {
          position.selectedTax = defaultTax
          position.tax_id = defaultTax.id
          position.tax_value = defaultTax.value
        }
      }
    },
    updatePosition(position, value, param, maxValue = null, minValue = null) {
      if (value && ((maxValue || maxValue === 0) && value > maxValue)) {
        value = maxValue
      }

      if (value && ((minValue || minValue === 0) && value < minValue)) {
        value = minValue
      }

      if (value && minValue === 0 && value === '-') {
        value = null
      }

      position.isUpdated = !!position.id

      if (param === 'name') {
        return false
      }

      if (param === 'code') {
        position.raw_code = this.generateRawCode(position.code)
        return false
      }

      if (param === 'qty') {
        if (value === '-') {
          value = null
        }
      }

      position[param] = value

      this.calculatePositionSummary(position)
      this.updateSummary()
    },
    calculatePositionSummary(position) {
      const qty = Number(position.qty ?? 0)
      const purchasePrice = Number(position.purchase_price ?? 0)
      const taxValue = Number(position.selectedTax?.value ?? 0)

      const rawNet = purchasePrice * qty

      const net = this.formatDecimal(rawNet, false, 2)

      const tax = this.formatDecimal(net * (taxValue / 100), false, 2)

      const total = this.formatDecimal(net + tax, false, 2)

      position.sum = net
      position.tax_sum = tax
      position.total = total
    },

    updateSummary() {
      let positionsSum = 0
      let positionsTaxSum = 0
      if (this.positions?.length) {
        const activePositions = [...this.positions].filter(position => position.status === 1)

        activePositions?.forEach(position => {
          const sum = position.sum || 0
          const taxSum = position.tax_sum || 0

          positionsSum += sum
          positionsTaxSum += taxSum
        })
      } else {
        this.isTaxCustom = false
        this.summary.tax = 0
        this.summary.adjustment = 0
      }

      this.summary.sum = this.formatDecimal(positionsSum, false)

      if (!this.isTaxCustom) {
        this.summary.tax = this.formatDecimal(positionsTaxSum, false)
      }

      this.summary.grand_total = this.formatDecimal(this.summary.sum + this.summary.tax, false)

      if (this.summary.adjustment && +this.summary.adjustment) {
        this.summary.grand_total += +this.summary.adjustment
      }
    },
    changeTax(position, newTax) {
      position.isUpdated = !position.isNew

      position.selectedTax = newTax ?? null
      position.tax_id = newTax?.id ?? null

      const v = newTax?.value
      position.tax_value = (v === 0 || v === '0') ? 0 : (v ? +v : null)

      position.valid_until = newTax?.valid_until ?? null

      const hasPrice = position.purchase_price !== null && position.purchase_price !== undefined && position.purchase_price !== ''
      const hasQty   = position.qty !== null && position.qty !== undefined && position.qty !== ''

      if (hasPrice && hasQty) {
        this.calculatePositionSummary(position)
      }

      this.updateSummary()
    },
    removePosition(position, index) {
      if (position.type === 4) {
        this.removeRequestPositionId = position.order_position_id
      } else if (position.type === 5 || position.type === 7) {
        this.removeReducedPositionId = position.reduced_position_id
      }
      setTimeout(() => {
        this.removeRequestPositionId = null
        this.removeReducedPositionId = null
        // this.removeWarehouseRequestId = null
      }, 10)

      this.positions.splice(index, 1)
      this.updateSummary()
    },
    removeRequestPosition(positionId) {
      this.positions = this.positions.filter(p => p.order_position_id !== positionId)
      this.updateSummary()
    },
    removeReducedPosition(positionId) {
      this.positions = this.positions.filter(p => p.reduced_position_id !== positionId)
      this.updateSummary()
    },
    // removeWarehouseRequestPosition(positionId) {
    //   this.positions = this.positions.filter(p => p.reduced_position_id !== positionId)
    //   this.updateSummary()
    // },
    confirmPositionDelete(positionToDelete) {
      this.positionToDelete = positionToDelete
      this.confirmPositionDeleteModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    addRequestPositions() {
      this.requestPositionsModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    addReducedPositions() {
      this.ReducedPositionsModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    closeRequestPositionsModal() {
      this.requestPositionsModal = false
      this.$store.commit('toggleSecondLayer', false)
    },
    closeReducedPositionsModal() {
      this.ReducedPositionsModal = false
      this.$store.commit('toggleSecondLayer', false)
    },
    closeConfirmPositionDeleteModal() {
      this.positionToDelete = {}
      this.confirmPositionDeleteModal = false
      this.$store.commit('toggleSecondLayer', false)
    },
    async deletePosition() {
      this.modalDataIsLoading = true
      const obj = {
        positionId: this.positionToDelete.id,
        qty: this.positionToDelete.qty,
        reducedPositionDataId: this.positionToDelete.order_position_id,
        reducedPositionId: this.positionToDelete.reduced_position_id,
        warehouseItemId: this.positionToDelete.warehouse_item_id,
        invoiceId: this.itemData.id,
      }

      try {
        const { status, data } = await httpClient.post('purchase-invoice/delete-position', obj)
        if (status === 200 && data?.success) {
          this.positionToDelete.isNew = false
          this.positionToDelete.isUpdated = false

          this.positions = this.positions?.filter(p => p.id !== this.positionToDelete.id)
          this.updateSummary()

          if (this.positionToDelete.type === 4) {
            this.getRequestPositions()
          } else if (this.positionToDelete.type === 5) {
            this.getReducedPositions()
          }

          this.itemData.changed_after_export = data.changed_after_export ?? false

          this.closeConfirmPositionDeleteModal()
          this.$toast.add({severity:'success', detail: this.$t('Data deleted'), life: this.settings.toastLife})
          this.$emit('update-items', this.itemData.id)
        } else if (data?.error) {
          if (settings.isDevMode) console.log(data.error)
          this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife});
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.modalDataIsLoading = false
      }
    },
    async saveItem() {
      this.submitted = true
      let wrongInputs = false
      let nameIsEmpty = false
      let purchasePriceIsEmpty = false
      let qtyIsEmpty = false
      let noTaxSelected = false
      let noWarehouseItemSelected = false
      let noAccountingCodeSelected = false
      let purchasePriceIsHigh = false

      if (!this.issuedDateIsValid ||
          !this.paymentDueDateIsValid ||
          this.invoiceNumberExists ||
          !this.selectedSupplier ||
          !this.itemData.number ||
          !this.issued ||
          !this.payment_due ||
          this.payment_due < this.issued ||
          !this.selectedState ||
          (this.itemData.comment && this.itemData.comment.length > this.commentMaxChars)) {
        wrongInputs = true
      }

      const newRequestAndReducedPositions = []
      this.positions?.filter(position => position.status === 1)?.forEach(position => {
        if (!position.id && (position.type === 4 || position.type === 5)) {
          newRequestAndReducedPositions.push({
            order_position_id: position.order_position_id,
            reduced_position_id: position.reduced_position_id,
            type: position.type,
            qty: position.qty
          })
        }
        if (this.accountingIntegrationConnected && this.exportInvoiceToAccounting && !position.selectedAccountingCode) {
          noAccountingCodeSelected = true
          wrongInputs = true
        } else if ((position.type === 1 || position.type === 7) && !position.selectedWarehouseItem) {
          noWarehouseItemSelected = true
          wrongInputs = true
        } else if (!position.name) {
          nameIsEmpty = true
          wrongInputs = true
        } else if (!position.qty) {
          qtyIsEmpty = true
          wrongInputs = true
        } else if (!position.purchase_price) {
          purchasePriceIsEmpty = true
          wrongInputs = true
        } else if (!position.selectedTax) {
          noTaxSelected = true
          wrongInputs = true
        } else if (position.selectedWarehouseItem && position.purchase_price > position.selectedWarehouseItem.purchase_price && !this.ignorePurchasePriceIsTooHigh) {
          purchasePriceIsHigh = true
        }
      })

      if (noAccountingCodeSelected) {
        this.$toast.add({ severity: 'error', detail: this.$t('noAccountingCodeSelectedMsg'), life: settings.toastLife })
      } else if (noWarehouseItemSelected) {
        this.$toast.add({ severity: 'error', detail: this.$t('lineItemNoStockItemSelectedMsg'), life: settings.toastLife })
      } else if (nameIsEmpty) {
        this.$toast.add({ severity: 'error', detail: this.$t('lineItemNameRequiredMsg'), life: settings.toastLife })
      } else if (qtyIsEmpty) {
        this.$toast.add({ severity: 'error', detail: this.$t('lineItemQtyRequiredMsg'), life: settings.toastLife })
      } else if (purchasePriceIsEmpty) {
        this.$toast.add({ severity: 'error', detail: this.$t('lineItemPurchasePriceRequiredMsg'), life: settings.toastLife })
      } else if (noTaxSelected) {
        this.$toast.add({ severity: 'error', detail: this.$t('lineItemTaxRequiredMsg'), life: settings.toastLife })
      }

      if (wrongInputs) {
        return false
      }

      if (purchasePriceIsHigh) {
        this.openConfirmPurchasePriceIsTooHighModal()
        return false
      }

      this.dataIsSending = true
      this.disableSaveButton = true


      const thereArePositions = this.positions?.length
      const purchaseInvoice = {
        id: this.itemData.id ? this.itemData.id : null,
        number: this.itemData.number ? this.itemData.number.trim() : null,
        state: this.selectedState ? this.selectedState.num : null,
        supplier_id: this.selectedSupplierId,
        sum: thereArePositions ? this.formatDecimal(this.summary.sum, false) : 0,
        tax: thereArePositions ? this.formatDecimal(this.summary.tax, false) : 0,
        is_tax_custom: this.isTaxCustom && thereArePositions ? 1 : 0,
        adjustment: thereArePositions ? this.formatDecimal(this.summary.adjustment, false) : 0,
        grand_total: thereArePositions ? this.formatDecimal(this.summary.grand_total, false) : 0,
        comment: this.itemData.comment ? this.itemData.comment.trim() : null,
        exportInvoiceToAccounting: this.accountingIntegrationConnected && this.exportInvoiceToAccounting,
        newPositions: this.generateNewPositionsData(),
        updatedPositions: this.generateUpdatedPositionsData(),
        issued: this.issued
            ? Math.floor(this.issued.getTime() / 1000)
            : Math.floor(Date.now() / 1000),
        payment_due: this.payment_due
            ? Math.floor(this.payment_due.getTime() / 1000)
            : Math.floor(Date.now() / 1000),
        newRequestAndReducedPositions
      }

      if (this.itemData.id) {
        try {
          const { status, data } = await httpClient.post(`purchase-invoice/update`, purchaseInvoice)
          if (status === 200 && data?.success) {
            this.$emit('update-items', this.itemData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: this.settings.toastLife});
            this.close()
            if (this.exportInvoiceToAccounting && data?.export_error_message) {
              this.$toast.add({
                severity:'error',
                summary: this.$t('invoiceExportErrorTitle'),
                detail: data.export_error_message,
                life: this.settings.toastLife * 2
              });
            }
          } else if (data?.invoice_number_exists) {
            this.invoiceNumberExists = true
            this.$toast.add({
              severity: 'error',
              detail: this.$t('invoiceNumberExists'),
              life: this.settings.toastLife
            })
          } else if (data?.exceeds_fixed_qty_positions) {
            this.markExceedsFixedQtyPositions(data.exceeds_fixed_qty_positions)
            this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('lineItemQtyExceedsMaxAllowedMsg'), life: this.settings.toastLife});
          } else if (data?.error) {
            if (settings.isDevMode) console.log(data.error)
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife});
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.disableSaveButton = false
          this.dataIsSending = false
          this.ignorePurchasePriceIsTooHigh = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`purchase-invoice/create`, purchaseInvoice)
          if (status === 201 && data?.success) {
            this.itemData.id = data.id
            this.$emit('update-items', this.itemData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: this.settings.toastLife});
            this.close()
            if (this.exportInvoiceToAccounting && data?.export_error_message) {
              this.$toast.add({
                severity:'error',
                summary: this.$t('invoiceExportErrorTitle'),
                detail: data.export_error_message,
                life: this.settings.toastLife * 2
              });
            }
          } else if (data?.invoice_number_exists) {
            this.invoiceNumberExists = true
            this.$toast.add({
              severity: 'error',
              detail: this.$t('invoiceNumberExists'),
              life: this.settings.toastLife
            })
          } else if (data?.exceeds_fixed_qty_positions) {
            this.markExceedsFixedQtyPositions(data.exceeds_fixed_qty_positions)
            this.$toast.add({
              severity:'error',
              summary: this.$t('Error'),
              detail: this.$t('lineItemQtyExceedsMaxAllowedMsg'),
              life: this.settings.toastLife
            })
          } else if (data?.error) {
            if (settings.isDevMode) console.log(data.error)
            this.$toast.add({
              severity:'error',
              summary: data.error.summary,
              detail: data.error.detail,
              life: this.settings.toastLife
            })
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.disableSaveButton = false
          this.dataIsSending = false
          this.ignorePurchasePriceIsTooHigh = false
        }
      }
    },
    markExceedsFixedQtyPositions(exceedsFixedQtyPositions) {
      if (!this.positions?.length) return
      exceedsFixedQtyPositions?.forEach(p => {
        const order_position_id = p.order_position_id
        const reduced_position_id = p.reduced_position_id
        const maxQty = p.max_qty

        let foundInvoicePosition = null
        if (order_position_id) {
          foundInvoicePosition = this.positions.find(p => !p.id && p.order_position_id === order_position_id)
        } else if (reduced_position_id) {
          foundInvoicePosition = this.positions.find(p => !p.id && p.reduced_position_id === reduced_position_id)
        }

        if (foundInvoicePosition) {
          const type = foundInvoicePosition.type
          foundInvoicePosition.unfixed_qty = maxQty ?? 0
          if (type === 4) {
            foundInvoicePosition.fixedPositionMaxQty = maxQty
          } else if (type === 5) {
            foundInvoicePosition.fixedPositionMinQty = -maxQty
          }
        }
      })
    },
    isSortableRowsDisabled() {
      return !!(
          this.closedFinancialPeriodDataEditingDisabled ||
          this.modalDataIsLoading ||
          this.dataIsSending
      )
    },
    isSortableRowVisible(position) {
      return !!(position && position.status)
    },
    // resetExceedsFixedQtyPositions() {
    //   // this.invoiceHasPositionsWithExceedsFixedQty = false
    //   this.positions?.filter(pos => pos.exceedsFixedQty)?.forEach(p => {
    //     // p.exceedsFixedQty = false
    //     p.fixedPositionMaxQty = p.ordered_qty - p.fixed_qty
    //   })
    // },
    close() {
      this.$emit('close')
    }
  },
  computed: {
    accountingExportLabel() {
      return this.$t('exportInvoiceToAccountingLabel', {
        provider: this.accountingIntegrationName,
      })
    },
    selectedSupplierId() {
      return this.selectedSupplier?.id ? +this.selectedSupplier.id : null
    },
    showClosedPeriodEditBanner() {
      return this.canUsePeriodLock
          && !!this.financialPeriodHasBeenClosed
    },

    showIssuedDateClosedWarning() {
      return this.canUsePeriodLock
          && !!this.issuedDateFinancialPeriodHasBeenClosed
          && !this.financialPeriodHasBeenClosed
    },

    closedFinancialPeriodDataEditingDisabled() {
      return this.canUsePeriodLock
          && this.financialPeriodHasBeenClosed
          && !this.closedFinancialDataEditingEnabled
    },
    showErrorColumn() {
      if (!this.positions) return false
      return !!this.positions.find(p => p.type === 1 && p.selectedWarehouseItem && p.purchase_price > p.selectedWarehouseItem.purchase_price)
    },
    showOrderHeader() {
      return !!this.positions?.find(p => p.status && p.order_position_id)
    },
    supplierChangingDisabled() {
      return !!this.positions?.find(position => position.status === 1 && (position.type === 4 || position.type === 5) || (position.type === 6 && !position.isNew))
    },
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '95%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '90%', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: 0, width: '90%', maxWidth: '1600px'}
      }
    },
    noPositions() {
      return !this.positions.find(position => position.status === 1)
    },
    invoiceHasNotNewPositions() {
      return !!this.positions.find(position => position.status === 1 && !position.isNew)
    }
  },
}
</script>

<style scoped lang="scss">
.dropdown-wrapper {
  display: flex;
}
.dropdown__select {
  width: 100%;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.dropdown__select--one-button-width {
  width: calc(100% - 2.357rem);
}
.dropdown__buttons {
  display: flex;
}
.dropdown__create-button{
  width: 2.357rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.custom-tax-input {
  font: inherit;
  color: inherit;
  margin: 4px auto;
  height: 26px;
  width: 90%;
  text-align: right!important;
  border: none;
  outline: none;
  &:focus,
  &:active {
    border: none;
    outline: none;
  }
}

.dropdown-wrapper {
  display: flex;
}

.dropdown__select {
  width: 100%;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.dropdown__select--border-radius {
  width: 100%;
  border-top-right-radius: 3px;
  border-bottom-right-radius: 3px;
}

.dropdown__select--one-button-width {
  width: calc(100% - 2.357rem);
}

#purchase-invoice-modal-supplier {
  flex: 1 1 0%;
  min-width: 0;
}

#purchase-invoice-modal-supplier ~ .dropdown__buttons {
  flex: 0 0 auto;
}
</style>
